import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiDelete, SERVICE_PATHS } from '@/lib/api-client';
import type { LlmConfig } from '@/types';
import type { LlmConfigFormData } from '@/schemas/llm-config.schema';

const LLM_CONFIGS_KEY = ['llm-configs'] as const;

/**
 * Fetches all LLM configurations.
 */
export function useLlmConfigs() {
  return useQuery<LlmConfig[]>({
    queryKey: LLM_CONFIGS_KEY,
    queryFn: () => apiGet<LlmConfig[]>(SERVICE_PATHS.llmConfig),
    staleTime: 30_000,
  });
}

/**
 * Creates a new LLM configuration.
 */
export function useCreateLlmConfig() {
  const queryClient = useQueryClient();

  return useMutation<LlmConfig, Error, LlmConfigFormData>({
    mutationFn: (data) => apiPost<LlmConfig>(SERVICE_PATHS.llmConfig, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LLM_CONFIGS_KEY });
    },
  });
}

/**
 * Updates an existing LLM configuration by serviceId.
 */
export function useUpdateLlmConfig() {
  const queryClient = useQueryClient();

  return useMutation<LlmConfig, Error, { serviceId: string; data: LlmConfigFormData }>({
    mutationFn: ({ serviceId, data }) =>
      apiPut<LlmConfig>(`${SERVICE_PATHS.llmConfig}/${serviceId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LLM_CONFIGS_KEY });
    },
  });
}

/**
 * Deletes an LLM configuration by serviceId.
 */
export function useDeleteLlmConfig() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (serviceId) =>
      apiDelete<void>(`${SERVICE_PATHS.llmConfig}/${serviceId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LLM_CONFIGS_KEY });
    },
  });
}
